/**
 * Landing mid-page sections, in page order:
 *   TrustSection      — "TRUSTED BY PROFESSIONALS FROM" marquee + trust cards + stats band
 *   PlatformSection   — "One Platform. Every Financial Need." tools + services rails
 *   TestimonialsSection — "Trusted by 500+ customers." forest band
 *   HowItWorksSection — 6 steps + "Start with your Income Tax Return" CTA banner
 */
import { TRUSTED_BY_LOGOS, TRUST_CARDS, STATS, FREE_TOOLS, SERVICES, TESTIMONIALS, HOW_IT_WORKS } from './data';
import { CardMarquee, CtaLink, Kicker, LogoMarquee, SerifHeading, Stars } from './shared';

// ── Trust: logos + cards + stats ─────────────────────────────────────────────

export function TrustSection() {
  return (
    <section className="relative overflow-hidden bg-white">
      {/* Lower half of the decorative ring that crosses the hero boundary —
          same circle as the hero's bottom-left ring (Figma). */}
      <div
        className="pointer-events-none absolute -left-[220px] -top-[151px] hidden h-[470px] w-[470px] rounded-full border-[90px] border-forest lg:block"
        aria-hidden
      />
      {/* Mobile Figma: a smaller ring pokes in at the left edge beside the
          stats stack (instead of the big boundary ring). */}
      <div
        className="pointer-events-none absolute -left-24 bottom-[440px] h-48 w-48 rounded-full border-[36px] border-forest lg:hidden"
        aria-hidden
      />
      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
        <Kicker className="text-center">Trusted by professionals from</Kicker>
        <div className="mt-6">
          <LogoMarquee logos={TRUSTED_BY_LOGOS} fade="white" />
        </div>

        <h2 className="mt-14 text-center font-landing text-3xl font-bold text-forest sm:text-4xl">
          Built on trust. Backed by experts.
        </h2>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {TRUST_CARDS.map((card) => (
            <div
              key={card.title}
              className="rounded-2xl border border-border bg-cream px-5 py-7 text-center"
            >
              <p className="text-lg font-bold text-forest">{card.title}</p>
              <p className="mt-1 text-sm text-sage">{card.sub}</p>
            </div>
          ))}
        </div>

        {/* Stats band — one forest strip on desktop, stacked cards on mobile */}
        <div className="mt-10 hidden overflow-hidden rounded-2xl bg-forest lg:grid lg:grid-cols-5">
          {STATS.map((s, i) => (
            <div
              key={s.label}
              className={`px-6 py-7 text-center ${i > 0 ? 'border-l border-hero-border/40' : ''}`}
            >
              <p className="text-4xl font-bold tracking-tight text-amber">{s.value}</p>
              <p className="mt-1.5 text-xs text-hero-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:hidden">
          {STATS.map((s) => (
            <div key={s.label} className="rounded-2xl bg-forest px-6 py-7 text-center">
              <p className="text-4xl font-bold tracking-tight text-amber">{s.value}</p>
              <p className="mt-1.5 text-xs text-hero-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <CtaLink />
        </div>
      </div>
    </section>
  );
}

// ── One Platform: tools + services ───────────────────────────────────────────

export function PlatformSection() {
  return (
    <section className="bg-parchment">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="text-center">
          <Kicker>One platform. Every financial need.</Kicker>
          <SerifHeading className="mt-4" line1="One Platform." line2="Every Financial Need." />
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-sage sm:text-base">
            Whether you&apos;re filing your Income Tax Return, planning taxes for the year,
            understanding a notice, or simply looking for financial guidance, FinminOS brings
            everything together in one secure platform.
          </p>
        </div>

        {/* Free tools */}
        <h3 className="mt-14 text-center font-body text-lg font-bold tracking-wide text-forest">
          FREE FINANCIAL TOOLS
        </h3>
        {/* repeat=2: five tool cards (~1500px) are narrower than wide viewports */}
        <CardMarquee className="mt-6" repeat={2}>
          {FREE_TOOLS.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="w-[340px] shrink-0 rounded-2xl bg-forest p-5 sm:w-[320px]"
            >
              <Icon className="h-6 w-6 text-cream" aria-hidden />
              <p className="mt-3 text-base font-bold text-cream">{title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-hero-muted-foreground">{desc}</p>
            </div>
          ))}
        </CardMarquee>

        {/* Services */}
        <h3 className="mt-14 text-center font-body text-lg font-bold tracking-wide text-forest">
          FINANCIAL SERVICES
        </h3>
        <CardMarquee className="mt-6">
          {SERVICES.map((svc, i) => (
            <div
              key={svc.title}
              className="w-[340px] shrink-0 rounded-[25px] border border-border bg-cream p-7 sm:w-[420px]"
            >
              {/* Mobile Figma uses a small number badge; desktop the big circle */}
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-forest text-base font-bold text-cream sm:h-20 sm:w-20 sm:text-3xl">
                {i + 1}
              </span>
              <p className="mt-5 text-xl font-bold text-forest sm:text-2xl">{svc.title}</p>
              <p className="mt-2 text-base leading-relaxed text-sage">{svc.desc}</p>
            </div>
          ))}
        </CardMarquee>

        <div className="mt-12 text-center">
          <CtaLink />
        </div>
      </div>
    </section>
  );
}

// ── Testimonials ──────────────────────────────────────────────────────────────

export function TestimonialsSection() {
  return (
    <section className="relative overflow-hidden bg-forest-deep">
      <div
        className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full border-[40px] border-black/15"
        aria-hidden
      />

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="text-center">
          <Kicker>Customer stories</Kicker>
          <SerifHeading
            dark
            className="mt-4"
            line1="Trusted by 500+ customers."
            line2="Built for relationships, not just returns"
          />
          <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-hero-muted-foreground sm:text-base">
            From first-time filers to startup founders, thousands of financial decisions have
            been reviewed through FinminOS. Our goal isn&apos;t just to file your Income Tax
            Return. It&apos;s to become the platform you rely on throughout the year.
          </p>
        </div>

        <CardMarquee className="mt-12">
          {TESTIMONIALS.map((t) => (
            <figure
              key={t.name}
              className="flex w-[330px] shrink-0 flex-col rounded-[18px] border border-border bg-white p-5 sm:w-[340px]"
            >
              <figcaption className="flex items-center gap-2.5">
                <img
                  src={t.avatar}
                  alt=""
                  className="h-12 w-12 rounded-full object-cover"
                />
                <span className="rounded-full bg-mint px-3.5 py-1.5 text-xs font-semibold text-forest">
                  {t.name}
                </span>
              </figcaption>
              <div className="mt-2.5">
                <Stars count={t.stars} />
              </div>
              <blockquote className="mt-3 flex-1 text-sm italic leading-relaxed text-forest/90">
                <span className="font-bold text-amber">&ldquo;</span> {t.quote}
              </blockquote>
              <p className="mt-4 text-xs text-sage">{t.role}</p>
              <p className="text-xs font-semibold text-forest">{t.city}</p>
            </figure>
          ))}
        </CardMarquee>
      </div>
    </section>
  );
}

// ── How FinminOS works ────────────────────────────────────────────────────────

export function HowItWorksSection() {
  return (
    <section className="bg-cream">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="text-center">
          <Kicker>How FinminOS works</Kicker>
          <SerifHeading className="mt-4" line1="From sign-up to year-round" line2="financial guidance" />
          <p className="mt-5 text-sm text-sage sm:text-base">
            Getting started takes only a few minutes. Your financial relationship lasts throughout the year.
          </p>
        </div>

        {/* Mobile Figma: horizontal swipe rail with peeking neighbours;
            desktop: the 3×2 grid. */}
        <div className="landing-rail -mx-4 mt-12 flex gap-4 overflow-x-auto px-4 sm:mx-0 sm:px-0 lg:grid lg:grid-cols-3 lg:overflow-visible">
          {HOW_IT_WORKS.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="w-[330px] shrink-0 rounded-2xl border border-border bg-white/50 p-6 lg:w-auto"
            >
              <Icon className="h-6 w-6 text-forest" aria-hidden />
              <p className="mt-3 text-base font-bold text-forest">{title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>

        {/* CTA banner */}
        <div className="mt-12 flex flex-col items-center justify-between gap-6 rounded-2xl bg-forest px-7 py-8 sm:px-10 lg:flex-row">
          <p className="font-landing text-xl font-semibold italic leading-snug text-cream sm:text-2xl">
            Start with your Income Tax Return.{' '}
            <em className="text-amber">Stay for every financial decision afterwards.</em>
          </p>
          <CtaLink size="lg" className="shrink-0" />
        </div>
      </div>
    </section>
  );
}
